import { set } from 'vue';
import { createContributor } from '@/data-provider/refDataProvider';
import { creatorNameComponents, fullNameFromComponents } from '@/foundations/helpers/contributorName';
import eventBus from '@/foundations/services/eventBus';
import { CREATOR_ROLES, type BookCreatorDraft, type BookCreators } from '@/types/bookCreator';

const creatorRows = (book: BookCreators): BookCreatorDraft[] => CREATOR_ROLES.flatMap((role) => book[role] ?? []);

const rowsToCreateByName = (book: BookCreators): BookCreatorDraft[][] => {
  const byName = new Map<string, BookCreatorDraft[]>();

  creatorRows(book)
    .filter((row) => !row.contributorId)
    .forEach((row) => {
      const name = fullNameFromComponents(creatorNameComponents(row)).toLowerCase();
      if (name) {
        byName.set(name, [...(byName.get(name) ?? []), row]);
      }
    });

  return [...byName.values()];
};

/**
 * Turns the book's creator rows into contributors. A row that is not linked to
 * one - nothing was picked from the search results - becomes a new contributor,
 * and the new id is written back onto the row so the book payload carries the
 * link. Rows sharing a name, the same person as author and narrator say, are
 * created once and share the id.
 */
export const createMissingContributors = async (book: BookCreators) => {
  const toCreate = rowsToCreateByName(book);
  if (toCreate.length === 0) {
    return;
  }

  try {
    await Promise.all(
      toCreate.map(async (rowsWithSameName) => {
        const contributorId = await createContributor(creatorNameComponents(rowsWithSameName[0]));
        rowsWithSameName.forEach((row) => {
          // `set`, so the form sees the row it just had a contributor created for
          set(row, 'contributorId', contributorId);
        });
      }),
    );
  } catch (error: any) {
    eventBus.$emit('snackbarV2.open', {
      text: `Could not create contributors: ${error?.message ?? error}. The book was not saved.`,
      color: 'error',
    });
    throw error;
  }

  eventBus.$emit('snackbarV2.open', {
    text: `Created ${toCreate.length} new contributor${toCreate.length > 1 ? 's' : ''}`,
    color: 'success',
  });
};

